/* ────────────────────────────────────────────────────────────────────
   /dashboard/vendor-sales — Mike's vendor-sale ledger
   Log a sale from any Market vendor. The system auto-calculates:
     - Mike's commission  = gross × my_pct
     - Member's 50% cut   = my_commission × 50%
   Status moves pending → cleared → paid. When PAID, the referring
   member's affiliate_lifetime_sales is incremented automatically.
   ──────────────────────────────────────────────────────────────────── */
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";

const API = process.env.REACT_APP_BACKEND_URL;

const G = "#2E9F3A", O = "#F39A1F", RED = "#C9202E", INK = "#0F172A";

const VENDORS = [
  { slug: "5-minute-garden",   label: "5-Minute Garden",    pct: 75  },
  { slug: "upnourish",         label: "UpNourish",          pct: 10  },
  { slug: "wellfit-treadmills",label: "WellFit Treadmills", pct: 5   },
  { slug: "goli",              label: "Goli",               pct: 15  },
  { slug: "bee-and-you",       label: "Bee & You",          pct: 20  },
  { slug: "gogreen-hemp",      label: "GoGreen Hemp",       pct: 15  },
  { slug: "piurify",           label: "Piurify",            pct: 10  },
  { slug: "beachrc",           label: "BeachRC.com",        pct: 25  },
  { slug: "faitharama-books",  label: "Faitharama Books",   pct: 100 },
  { slug: "faitharama-academy",label: "Faitharama Academy", pct: 100 },
  { slug: "affiliate-101",     label: "Affiliate 101",      pct: 100 },
];

const STATUS_BADGE = {
  pending: { bg: O,   label: "PENDING" },
  cleared: { bg: "#1E64C4", label: "CLEARED" },
  paid:    { bg: G,   label: "PAID" },
};

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function VendorSalesAdminPage() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [totals, setTotals] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [form, setForm] = useState({
    vendor_slug: "5-minute-garden",
    gross: "",
    ref_code: "",
    buyer_email: "",
    external_order_id: "",
    notes: "",
  });

  const authHeader = () => {
    const t = localStorage.getItem("faitharama_token");
    return { "Content-Type": "application/json", Authorization: `Bearer ${t || ""}` };
  };

  async function load() {
    try {
      const r = await fetch(`${API}/api/admin/vendor-sales?limit=300`, { headers: authHeader() });
      const j = await r.json();
      setSales(j.sales || []);
      setTotals(j.totals || {});
    } catch (e) { console.error(e); }
  }

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/vendor-sales?limit=300`, {
          headers: { "Content-Type": "application/json",
                     Authorization: `Bearer ${localStorage.getItem("faitharama_token") || ""}` },
        });
        const j = await r.json();
        if (cancelled) return;
        setSales(j.sales || []);
        setTotals(j.totals || {});
      } catch (e) { console.error(e); }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  async function setStatus(id, new_status) {
    setBusy(true);
    try {
      await fetch(`${API}/api/admin/vendor-sales/${id}/status?new_status=${new_status}`, {
        method: "POST", headers: authHeader(),
      });
      load();
    } finally { setBusy(false); }
  }

  async function submitSale(e) {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const r = await fetch(`${API}/api/admin/vendor-sales`, {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify({
          vendor_slug: form.vendor_slug,
          gross_amount_usd: parseFloat(form.gross || "0"),
          ref_code: form.ref_code || null,
          buyer_email: form.buyer_email || null,
          external_order_id: form.external_order_id || null,
          notes: form.notes || null,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Failed");
      setMsg(`✓ Logged ${form.vendor_slug} sale: Mike +${fmt(j.sale.my_commission_usd)}, Member +${fmt(j.sale.member_payout_usd)}`);
      setForm({ ...form, gross: "", ref_code: "", buyer_email: "", external_order_id: "", notes: "" });
      load();
    } catch (e) {
      setMsg(`✗ ${e.message}`);
    } finally { setBusy(false); }
  }

  const vendor = VENDORS.find(v => v.slug === form.vendor_slug);
  const previewGross = parseFloat(form.gross || "0") || 0;
  const previewMike = vendor ? (previewGross * vendor.pct / 100) : 0;
  const previewMember = previewMike * 0.5;

  return (
    <div className="min-h-screen bg-[#F6F9FC]" data-testid="vendor-sales-admin-page">
      <SiteNav />
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <h1 className="font-serif-display text-3xl md:text-5xl uppercase" style={{ color: INK }}>
          Vendor Sales Ledger
        </h1>
        <p className="text-base mt-2" style={{ color: INK, opacity: 0.75 }}>
          Log every commission Mike receives from a Market vendor. The member who referred the sale gets 50% of Mike&apos;s commission.
        </p>

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Tile data-testid="totals-gross"           label="Gross Sales"             value={fmt(totals.gross_usd)} accent={INK} />
          <Tile data-testid="totals-mike-commission" label="Mike's Commission"       value={fmt(totals.my_commission_usd)} accent={RED} />
          <Tile data-testid="totals-member-pending"  label="Members Owed (Pending)"  value={fmt(totals.pending_payout_usd)} accent={O} />
          <Tile data-testid="totals-member-paid"     label="Members Paid"            value={fmt(totals.paid_payout_usd)} accent={G} />
        </div>

        {/* Log form */}
        <form onSubmit={submitSale} className="mt-8 bg-white rounded-2xl p-5 md:p-6 shadow border" data-testid="log-sale-form">
          <h2 className="font-serif-display text-2xl uppercase mb-4" style={{ color: INK }}>Log A Sale</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Vendor">
              <select data-testid="field-vendor" required value={form.vendor_slug}
                      onChange={e => setForm({ ...form, vendor_slug: e.target.value })}
                      className="w-full border-2 rounded-lg px-3 py-2.5">
                {VENDORS.map(v => (
                  <option key={v.slug} value={v.slug}>{v.label} ({v.pct}% to Mike)</option>
                ))}
              </select>
            </Field>
            <Field label="Gross Sale Amount (USD)">
              <input data-testid="field-gross" required type="number" step="0.01" min="0.01" value={form.gross}
                     onChange={e => setForm({ ...form, gross: e.target.value })}
                     className="w-full border-2 rounded-lg px-3 py-2.5" placeholder="37.00" />
            </Field>
            <Field label="Member Ref Code (optional)">
              <input data-testid="field-ref-code" type="text" value={form.ref_code}
                     onChange={e => setForm({ ...form, ref_code: e.target.value.toUpperCase() })}
                     className="w-full border-2 rounded-lg px-3 py-2.5" placeholder="MIKE123" />
            </Field>
            <Field label="Buyer Email (optional)">
              <input data-testid="field-buyer-email" type="email" value={form.buyer_email}
                     onChange={e => setForm({ ...form, buyer_email: e.target.value })}
                     className="w-full border-2 rounded-lg px-3 py-2.5" placeholder="buyer@example.com" />
            </Field>
            <Field label="Vendor Order ID (optional)">
              <input data-testid="field-order-id" type="text" value={form.external_order_id}
                     onChange={e => setForm({ ...form, external_order_id: e.target.value })}
                     className="w-full border-2 rounded-lg px-3 py-2.5" placeholder="DS24-12345" />
            </Field>
            <Field label="Notes (optional)">
              <input data-testid="field-notes" type="text" value={form.notes}
                     onChange={e => setForm({ ...form, notes: e.target.value })}
                     className="w-full border-2 rounded-lg px-3 py-2.5" />
            </Field>
          </div>

          {/* Live preview */}
          <div className="mt-4 p-3 rounded-lg" style={{ background: "#F3F8F3", border: `2px dashed ${G}` }}>
            <div className="text-xs uppercase tracking-wider font-bold" style={{ color: G }}>Preview</div>
            <div className="mt-1 text-sm" style={{ color: INK }}>
              Gross <strong>{fmt(previewGross)}</strong> × {vendor?.pct}% = Mike <strong>{fmt(previewMike)}</strong> → Member <strong style={{ color: G }}>{fmt(previewMember)}</strong> (50% split)
              {!form.ref_code && <span className="text-orange-600 ml-2">⚠ No ref code — nothing credited to a member</span>}
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="submit-sale-btn"
                  className="mt-5 px-6 py-3 rounded-lg font-bold uppercase tracking-wider text-white disabled:opacity-50"
                  style={{ background: G }}>
            {busy ? "Saving…" : "Log Sale"}
          </button>
          {msg && <div className="mt-3 text-sm" style={{ color: msg.startsWith("✓") ? G : RED }}>{msg}</div>}
        </form>

        {/* Sales table */}
        <h2 className="font-serif-display text-2xl uppercase mt-10 mb-3" style={{ color: INK }}>Sales ({sales.length})</h2>
        <div className="bg-white rounded-2xl shadow border overflow-hidden" data-testid="sales-table-wrap">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "#F1F5F9" }}>
                <tr className="text-left">
                  <Th>Date</Th><Th>Vendor</Th><Th>Member</Th><Th>Gross</Th>
                  <Th>Mike %</Th><Th>Mike $</Th><Th>Member $</Th><Th>Status</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {sales.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-8 text-gray-500">No sales logged yet.</td></tr>
                )}
                {sales.map(s => {
                  const sb = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={s.id} className="border-t" data-testid={`sale-row-${s.id}`}>
                      <Td>{(s.created_at || "").slice(0, 10)}</Td>
                      <Td>{s.vendor_slug}</Td>
                      <Td>{s.referrer_email || s.ref_code || <span className="text-gray-400">—</span>}</Td>
                      <Td>{fmt(s.gross_amount_usd)}</Td>
                      <Td>{s.my_commission_pct}%</Td>
                      <Td>{fmt(s.my_commission_usd)}</Td>
                      <Td style={{ color: G, fontWeight: 700 }}>{fmt(s.member_payout_usd)}</Td>
                      <Td>
                        <span className="px-2 py-1 rounded-full text-[10px] font-bold text-white" style={{ background: sb.bg }}>
                          {sb.label}
                        </span>
                      </Td>
                      <Td>
                        <div className="flex gap-1.5">
                          {s.status === "pending" && (
                            <button onClick={() => setStatus(s.id, "cleared")} disabled={busy}
                                    data-testid={`btn-clear-${s.id}`}
                                    className="px-2 py-1 text-[11px] font-bold rounded text-white" style={{ background: "#1E64C4" }}>
                              Mark Cleared
                            </button>
                          )}
                          {s.status === "cleared" && (
                            <button onClick={() => setStatus(s.id, "paid")} disabled={busy}
                                    data-testid={`btn-pay-${s.id}`}
                                    className="px-2 py-1 text-[11px] font-bold rounded text-white" style={{ background: G }}>
                              Mark Paid
                            </button>
                          )}
                          {s.status === "paid" && <span className="text-[11px] text-gray-400">—</span>}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function Tile({ label, value, accent, ...rest }) {
  return (
    <div className="bg-white rounded-xl shadow border p-4 border-l-4" style={{ borderLeftColor: accent }} {...rest}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider font-bold mb-1.5" style={{ color: INK }}>{label}</span>
      {children}
    </label>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-bold text-[11px] uppercase tracking-wider text-gray-700">{children}</th>;
const Td = ({ children, style }) => <td className="px-3 py-2.5" style={style}>{children}</td>;
